import { RefreshCw } from "lucide-react";
import { useRouteError } from "react-router";

import { config } from "@/config";
import { log } from "@/core/log";
import { Button } from "@/ui/button";
import { Spinner } from "@/ui/spinner";

/**
 * Something on this screen failed: usually a new version was deployed (its old files are gone) or the network
 * dropped while the screen's code downloaded. `fullPage` when nothing around it (menu, header) is left.
 * Local builds also show the error itself, to fix it.
 */
export function RouteError({ fullPage = false }: { fullPage?: boolean }) {
  const error = useRouteError();
  log.warn("Screen failed", error);
  const card = (
    <div
      role="alert"
      className="flex max-w-lg flex-col items-start gap-3 rounded-2xl border border-border bg-surface p-6"
    >
      <p className="text-base font-bold">This page didn't load</p>
      <p className="text-sm text-muted">
        Brocoly may have just been updated, or the connection dropped. Reloading usually fixes it.
      </p>
      {config.appEnv === "local" && error instanceof Error && (
        <pre className="w-full overflow-x-auto rounded-lg bg-surface-sunken p-3 text-xs text-ink-2">
          {error.message}
        </pre>
      )}
      <Button variant="secondary" onClick={() => window.location.reload()}>
        <RefreshCw aria-hidden="true" />
        Reload
      </Button>
    </div>
  );
  return fullPage ? (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-4">{card}</div>
  ) : (
    card
  );
}

/** Shown while the first screen's code downloads. */
export function RouteLoading() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <Spinner label="Loading…" />
    </div>
  );
}
